/* eslint-disable no-undef */
/* =============================================================================
 * Properties & Units  +  shared Unit detail drawer
 * ===========================================================================*/
import React, { useState, useEffect, Fragment } from "react";
import { fmtMoney, fmtNum, fmtHcf, fmtDate, fmtShort, mgrColor, Icon, StatusPill, Pill, Toggle, Avatar, Sparkline, UsageBars, Drawer, DRow, Search, initials, Modal, useToast, ApiDown } from "./components.jsx";
import { DATA } from "./data.js";
import { fetchUnits, fetchUnitDetail, mapUnit, fetchSensors, mapSensor, swapMeter, isPulseCounterApp, isMultiCounterApp, updateUnit, fetchTemplates, fetchProperties, createUnit, deleteUnit, addReading, updateReading, deleteReading, fetchQboConnections, createProperty, updateProperty, deleteProperty } from "./api.js";
import { QboRefPicker } from "./qbo-picker.jsx";

// HCF to 2 decimals — the reading log wants meter precision (summary views keep the 1-decimal fmtHcf).
const fmtHcf2 = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// ------------------------------------------------------------ Live unit detail drawer (real API data)
function LiveUnitDrawer({ tenancyId, onClose, onChanged, go, projected, usage }) {
  const [d, setD] = useState(null);   // detail object · false = error · null = loading
  const [refresh, setRefresh] = useState(0);   // bump to refetch after a meter swap / edit / reading
  const [swapOpen, setSwapOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editRead, setEditRead] = useState(null);   // reading being edited (null = none)
  const [moveOutOpen, setMoveOutOpen] = useState(false);
  const [moveOutDate, setMoveOutDate] = useState("");
  const [moveAction, setMoveAction] = useState("bill");   // bill · later · close
  const [movingOut, setMovingOut] = useState(false);
  const toast = useToast();
  useEffect(() => {
    if (tenancyId == null) return;
    setD(null);
    let cancelled = false;
    fetchUnitDetail(tenancyId).then((x) => { if (!cancelled) setD(x); }).catch(() => { if (!cancelled) setD(false); });
    return () => { cancelled = true; };
  }, [tenancyId, refresh]);

  if (tenancyId == null) return null;
  const SRC = { 1: "Manual", 2: "Calculated", 3: "Estimated" };
  const INV_ST = { 0: ["Draft", "muted"], 1: ["Submitted", "sent"], 2: ["Sent", "sent"], 3: ["Paid", "ok"], 4: ["Failed", "danger"], 5: ["Void", "muted"] };
  const fmt = (s) => (s ? new Date(s).toLocaleDateString() : "—");
  const tenant = d ? ((d.tenantRaw || "").split(":")[2] || d.tenantRaw || "—").trim() : "";

  // Record the move-out date, then either bill now, arm the final bill for the move-out date, or just close.
  const doMoveOut = (act) => {
    setMovingOut(true);
    updateUnit(tenancyId, {
      qboCustomerName: d.tenantRaw, qboCustomerRef: d.qboCustomerRef || null,
      moveInDate: (d.moveInDate || "").slice(0, 10), moveOutDate,
      invoiceDay: d.invoiceDay, minPeriod: d.minPeriod,
      autoInvoice: d.autoInvoice, autoEmail: d.autoEmail, autoPay: d.autoPay,
      active: act === "close" ? false : d.active,   // "close" deactivates now; otherwise the final invoice does
      invoiceDefinitionId: d.invoiceDefinitionId,
    })
      .then(() => {
        setMovingOut(false); setMoveOutOpen(false);
        if (act === "bill") { onClose(); go && go("invoices", { create: true, unitId: tenancyId, readDate: moveOutDate }); return; }
        onChanged && onChanged(); setRefresh((n) => n + 1);
        toast(act === "later"
          ? { title: "Move-out scheduled", body: `${d.name} moves out ${fmt(moveOutDate)} — its final bill is created on your next run on or after that date.` }
          : { title: "Unit closed", body: `${d.name} moved out ${fmt(moveOutDate)} — no invoice created.` });
      })
      .catch(() => { setMovingOut(false); toast({ title: "Couldn't set move-out", body: "The API rejected the update — check the date and try again." }); });
  };
  const moveToday = new Date().toISOString().slice(0, 10);
  const moveOpts = moveOutDate > moveToday
    ? [{ key: "later", title: "Bill on the move-out date", desc: "Record the date now; the final bill is created on your next run on or after it." },
       { key: "bill", title: "Bill the final period now", desc: "Charge through the move-out date now and open the invoice to review." },
       { key: "close", title: "Close the unit, no bill", desc: "Just record the move-out and deactivate — no invoice." }]
    : [{ key: "bill", title: "Send the final bill now", desc: "Charge through the move-out date and open the invoice to review." },
       { key: "close", title: "Close the unit, no bill", desc: "Just record the move-out and deactivate — no invoice." }];
  const moveEff = moveOpts.some((o) => o.key === moveAction) ? moveAction : moveOpts[0].key;
  const moveCta = moveEff === "bill" ? "Continue to final bill" : moveEff === "later" ? "Set move-out date" : "Close unit";
  const delRead = (r) => {
    if (!window.confirm(`Delete the ${fmt(r.readAt)} reading (${fmtHcf(r.scaled)} HCF)? This can't be undone.`)) return;
    deleteReading(tenancyId, r.id)
      .then(() => { setRefresh((n) => n + 1); onChanged && onChanged(); toast({ title: "Reading deleted", body: "The reading was removed." }); })
      .catch(() => toast({ title: "Delete failed", body: "The API rejected the request." }));
  };

  return (
    <Drawer open={tenancyId != null} onClose={onClose} wide>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: "var(--accent)", fontSize: 14 }}>{d ? d.name.slice(0, 3) : "…"}</div>
        <div style={{ flex: 1 }}>
          <h2>{d ? d.name : "Loading…"}</h2>
          <div className="sub">{d ? `${d.propName} · ${d.managerName}` : ""}</div>
          {d && (
            <div className="wrap-gap" style={{ marginTop: 9 }}>
              <Pill kind="ok">Live</Pill>
              {d.active ? <Pill kind="auto">Active</Pill> : <Pill kind="muted">Inactive</Pill>}
              {d.autoPay && <Pill kind="scheduled">Auto-pay</Pill>}
            </div>
          )}
        </div>
        {d && go && <button className="btn btn--soft btn--sm" title="See this unit's usage over time" style={{ marginRight: 6 }}
          onClick={() => { onClose(); go("usage", { tenancyId }); }}><Icon name="chart" size={13} />Usage</button>}
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>
      <div className="drawer__body">
        {d === null && <div className="empty">Loading unit detail…</div>}
        {d === false && <div className="empty">Couldn't load unit detail from the API.</div>}
        {d && (
          <Fragment>
            {(projected != null || usage != null) && (
              <div className="card" style={{ padding: "12px 16px", boxShadow: "none", border: "1px solid var(--color-border-light)", marginBottom: 14, display: "flex", gap: 28 }}>
                <div><div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Projected next bill</div><b style={{ fontSize: 20, color: "var(--color-primary)" }}>{projected != null ? fmtMoney(projected) : "—"}</b></div>
                <div><div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>This period usage</div><b style={{ fontSize: 20 }}>{usage != null ? fmtHcf(usage) + " HCF" : "—"}</b></div>
              </div>
            )}
            <div className="row" style={{ gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {d.active && <button className="btn btn--soft btn--sm" onClick={() => setSwapOpen(true)}><Icon name="refresh" size={13} />Swap meter</button>}
              <button className="btn btn--soft btn--sm" onClick={() => setEditOpen(true)}><Icon name="edit" size={13} />Edit unit</button>
            </div>
            <div className="section-h" style={{ marginTop: 0 }}>Tenant</div>
            <DRow k="Tenant">{tenant}</DRow>
            <DRow k="Move-in">{fmt(d.moveInDate)}</DRow>
            <DRow k="Move-out">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                {d.moveOutDate ? fmt(d.moveOutDate)
                  : d.active ? <span className="muted">— current</span>
                  : <span className="muted" title="This tenancy is inactive but legacy never recorded its move-out date — a data-cleanup item.">— not recorded</span>}
                {d.active && <button className="btn btn--soft btn--sm" onClick={() => { const init = d.moveOutDate ? String(d.moveOutDate).slice(0, 10) : new Date().toISOString().slice(0, 10); setMoveOutDate(init); setMoveAction(init > new Date().toISOString().slice(0, 10) ? "later" : "bill"); setMoveOutOpen(true); }}>Move out</button>}
              </span>
            </DRow>

            <div className="section-h">Meter history</div>
            <table className="lines">
              <thead><tr><th>Sensor</th><th className="num">Counter</th><th>From</th><th>To</th></tr></thead>
              <tbody>
                {d.meters.map((m) => (
                  <tr key={m.id}>
                    <td className="mono">
                      <span className="sensor-link" title="Open sensor health"
                            onClick={() => { if (go) { onClose(); go("sensors", { sensorId: m.sensorId }); } }}>{m.sensorId}</span>
                    </td>
                    <td className="num">{m.counterIndex}</td>
                    <td>{fmt(m.effectiveFrom)}</td>
                    <td>{m.effectiveTo ? fmt(m.effectiveTo) : <Pill kind="ok">active</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="between" style={{ marginTop: 18, marginBottom: 4 }}>
              <div className="section-h" style={{ margin: 0 }}>Readings &amp; invoices · {d.readings.length}</div>
              <button className="btn btn--soft btn--sm" onClick={() => setAddOpen(true)}><Icon name="plus" size={13} />Add reading</button>
            </div>
            <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>
              The meter's running total (HCF) at manual register reads and billing points — usage is the change since the prior one. An <span className="pill pill--ok" style={{ fontSize: 9 }}>Inv&nbsp;#</span> tag is a v2 bill — click to open it; its amount shows at right. A <span className="pill pill--muted" style={{ fontSize: 9 }}>Billed</span> tag marks a read billed in the legacy system, whose itemized invoice wasn't migrated (no amount). Routine calculated reads — daily roll-ups of the raw pulses — are on the Usage page.{d.readings.length >= 24 ? " Showing the latest 24." : ""}
            </div>
            <table className="lines">
              <thead><tr><th>Date</th><th>Type</th><th className="num">Reading (HCF)</th><th className="num">Usage (HCF)</th><th className="num">Amount</th><th></th></tr></thead>
              <tbody>
                {d.readings.map((r, i) => {
                  const prev = d.readings[i + 1];   // readings are newest-first, so the next row is the older reading
                  const use = prev != null ? Math.max(0, r.scaled - prev.scaled) : null;
                  return (
                    <tr key={r.id ?? i}>
                      <td>{fmt(r.readAt)}</td>
                      <td><span className="row" style={{ gap: 5, flexWrap: "wrap" }}>
                        {r.isBaseline
                          ? <span className="pill pill--muted">Base</span>
                          : r.source !== 2 && <span className="pill pill--sent">{SRC[r.source] || r.source}</span>}
                        {r.invoiceId != null
                          ? <React.Fragment>
                              <span className="pill pill--ok" style={{ cursor: "pointer" }}
                                    title={`${(INV_ST[r.invoiceStatus] || ["Invoice"])[0]} · ${fmtMoney(r.invoiceAmount)} — click to open`}
                                    onClick={() => { onClose(); go && go("invoices", { invoiceId: r.invoiceId }); }}>
                                {r.invoiceDoc || `Inv #${r.invoiceId}`}
                              </span>
                              {/* external: straight to the QuickBooks document — no double hop via the Invoices screen */}
                              {r.invoiceQboId && (
                                <a className="iconbtn" style={{ width: 22, height: 22 }} href={`https://qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(r.invoiceQboId)}`}
                                   target="_blank" rel="noopener noreferrer" title={`Open invoice #${r.invoiceDoc || r.invoiceId} in QuickBooks Online (new tab)`}>
                                  <Icon name="external" size={12} />
                                </a>
                              )}
                            </React.Fragment>
                          : r.invoiced && <span className="pill pill--muted" title="Billed in the legacy system — recovered from the migrated reading flag. No itemized invoice record came across in the migration.">Billed</span>}
                      </span></td>
                      <td className="num strong">{fmtHcf2(r.scaled)}</td>
                      <td className="num">{fmtHcf2(use)}</td>
                      <td className="num">{r.invoiceId != null ? fmtMoney(r.invoiceAmount) : ""}</td>
                      <td className="num" style={{ whiteSpace: "nowrap", width: 1 }}>
                        {/* locked (server-computed): part of an ISSUED invoice or a legacy bill — the sent
                            document is the record; corrections go through voiding it in QuickBooks. */}
                        <span style={{ display: "inline-flex", gap: 4 }}>
                          {r.locked
                            ? <span title={`This reading is part of ${r.invoiceDoc ? `issued invoice #${r.invoiceDoc}` : r.invoiceId != null ? `invoice #${r.invoiceId}` : "a bill issued by the legacy system"}. Void the invoice in QuickBooks to unlock it.`}>
                                <button className="iconbtn" disabled style={{ opacity: .3, cursor: "not-allowed" }}><Icon name="edit" size={13} /></button>
                              </span>
                            : <button className="iconbtn" title="Edit reading" onClick={() => setEditRead(r)}><Icon name="edit" size={13} /></button>}
                          {r.locked
                            ? <span title="Billed readings can't be deleted — they're part of an issued invoice."><button className="iconbtn" disabled style={{ opacity: .3, cursor: "not-allowed" }}><Icon name="trash" size={13} /></button></span>
                            : <button className="iconbtn" title="Delete reading" onClick={() => delRead(r)}><Icon name="trash" size={13} /></button>}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!d.readings.length && <tr><td colSpan={6} className="muted" style={{ padding: "10px 2px", fontSize: 12 }}>No manual reads or billing points yet — only derived calculated reads, shown on the Usage page.</td></tr>}
              </tbody>
            </table>
          </Fragment>
        )}
      </div>
      <SwapMeterModal open={swapOpen} onClose={() => setSwapOpen(false)} tenancyId={tenancyId}
        current={d && d.meters ? d.meters.find((m) => !m.effectiveTo) : null}
        onDone={() => setRefresh((n) => n + 1)} />
      <EditUnitModal open={editOpen} onClose={() => setEditOpen(false)} detail={d && d.meters ? d : null}
        onDone={() => { setRefresh((n) => n + 1); onChanged && onChanged(); }}
        onDeleted={() => { onChanged && onChanged(); onClose(); }} />
      <AddReadingModal open={addOpen || !!editRead} edit={editRead} onClose={() => { setAddOpen(false); setEditRead(null); }} tenancyId={tenancyId}
        onDone={() => { setRefresh((n) => n + 1); onChanged && onChanged(); }} />
      <Modal open={moveOutOpen} onClose={() => setMoveOutOpen(false)} title={`Move out — ${d ? d.name : ""}`} width={470}>
        <div style={{ display: "grid", gap: 14 }}>
          <div className="field"><label>Move-out date</label><input className="input" type="date" value={moveOutDate} onChange={(e) => setMoveOutDate(e.target.value)} /></div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--color-muted)" }}>What should happen?</label>
            <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
              {moveOpts.map((o) => (
                <label key={o.key} className="row" style={{ gap: 10, alignItems: "flex-start", padding: "10px 12px", border: "1px solid " + (moveEff === o.key ? "var(--color-primary)" : "var(--color-border-light)"), borderRadius: 8, cursor: "pointer", background: moveEff === o.key ? "var(--color-primary-bg)" : "" }}>
                  <input type="radio" name="moveact" checked={moveEff === o.key} onChange={() => setMoveAction(o.key)} style={{ marginTop: 2 }} />
                  <span><b style={{ fontSize: 13 }}>{o.title}</b><div className="muted" style={{ fontSize: 11.5, lineHeight: 1.45 }}>{o.desc}</div></span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button className="btn btn--ghost" onClick={() => setMoveOutOpen(false)}>Cancel</button>
          <button className="btn btn--primary" disabled={movingOut || !moveOutDate} onClick={() => doMoveOut(moveEff)}>{movingOut ? "…" : moveCta}</button>
        </div>
      </Modal>
    </Drawer>
  );
}

// Edit a tenancy's billing settings (tenant name, dates, invoice cadence, auto flags, rate template, active).
function EditUnitModal({ open, onClose, detail, onDone, onDeleted }) {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    setConfirmDel(false);
    if (!open || !detail) return;
    fetchTemplates().then((t) => setTemplates(t)).catch(() => setTemplates([]));
    setF({
      qboCustomerName: detail.tenantRaw || "",
      qboCustomerRef: detail.qboCustomerRef || null,
      moveInDate: (detail.moveInDate || "").slice(0, 10),
      moveOutDate: detail.moveOutDate ? String(detail.moveOutDate).slice(0, 10) : "",
      invoiceDay: detail.invoiceDay,
      minPeriod: detail.minPeriod,
      invoiceDefinitionId: detail.invoiceDefinitionId,
      autoInvoice: detail.autoInvoice,
      autoEmail: detail.autoEmail,
      autoPay: detail.autoPay,
      active: detail.active,
    });
  }, [open, detail && detail.id]);
  if (!open || !f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  // Only this unit's company (realm) templates — plus whatever's currently assigned, so the select stays truthful.
  const realmTemplates = detail.realmId
    ? templates.filter((t) => String(t.realmId) === String(detail.realmId) || String(t.id) === String(f.invoiceDefinitionId))
    : templates;

  const save = () => {
    if (!f.moveInDate) { toast({ title: "Move-in required", body: "Enter a move-in date." }); return; }
    setSaving(true);
    updateUnit(detail.id, {
      qboCustomerName: f.qboCustomerName,
      qboCustomerRef: f.qboCustomerRef,
      moveInDate: f.moveInDate,
      moveOutDate: f.moveOutDate || null,
      invoiceDay: +f.invoiceDay,
      minPeriod: +f.minPeriod,
      autoInvoice: f.autoInvoice, autoEmail: f.autoEmail, autoPay: f.autoPay, active: f.active,
      invoiceDefinitionId: f.invoiceDefinitionId,
    })
      .then(() => { setSaving(false); onDone(); onClose(); toast({ title: "Unit saved", body: "Tenancy details updated." }); })
      .catch(() => { setSaving(false); toast({ title: "Save failed", body: "The API rejected the update — check the values and try again." }); });
  };

  const onDelete = () => {
    setDeleting(true);
    deleteUnit(detail.id)
      .then(() => { setDeleting(false); onDeleted(); toast({ title: "Unit deleted", body: `${detail.name} was removed.` }); })
      .catch((e) => {
        setDeleting(false); setConfirmDel(false);
        const msg = e && e.status === 409 && e.body && e.body.message ? e.body.message : "Couldn't delete this unit.";
        toast({ title: "Delete blocked", body: msg });
      });
  };

  const Flag = ({ label, k, hint }) => (
    <div className="between" style={{ padding: "6px 0", gap: 12 }}>
      <div><div style={{ fontSize: 13 }}>{label}</div>{hint && <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>{hint}</div>}</div>
      <Toggle on={f[k]} onChange={(v) => set(k, v)} />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Edit unit" width={520}>
      <div style={{ display: "grid", gap: 12 }}>
        <div className="field"><label>QBO customer</label>
          <QboRefPicker realmId={detail.realmId} type="Customer" value={f.qboCustomerRef} name={f.qboCustomerName}
            placeholder="Select or type a QuickBooks customer…"
            onChange={({ qboId, name }) => setF((x) => ({ ...x, qboCustomerRef: qboId, qboCustomerName: name }))} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field"><label>Move-in</label><input className="input" type="date" value={f.moveInDate} onChange={(e) => set("moveInDate", e.target.value)} /></div>
          <div className="field"><label>Move-out</label><input className="input" type="date" value={f.moveOutDate} onChange={(e) => set("moveOutDate", e.target.value)} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field"><label>Invoice day of month</label><input className="input" type="number" min="1" max="28" value={f.invoiceDay} onChange={(e) => set("invoiceDay", e.target.value)} /><span className="muted" style={{ fontSize: 11 }}>The day of the month this unit should be billed (1–28).</span></div>
          <div className="field"><label>Min days between bills</label><input className="input" type="number" min="0" value={f.minPeriod} onChange={(e) => set("minPeriod", e.target.value)} /><span className="muted" style={{ fontSize: 11 }}>Won't create a new bill until this many days after the last one.</span></div>
        </div>
        <div className="field"><label>Rate template</label>
          <select className="select" value={f.invoiceDefinitionId} onChange={(e) => set("invoiceDefinitionId", e.target.value)}>
            {realmTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div style={{ borderTop: "1px solid var(--color-border-light)", paddingTop: 4 }}>
          <Flag label="Auto-invoice" k="autoInvoice" />
          <Flag label="Auto-email invoice" k="autoEmail" />
          <Flag label="Auto-pay" k="autoPay" hint="Tenant pays automatically — the bill uses the auto-pay memo and isn't sent for manual payment." />
          <Flag label="Active" k="active" />
        </div>
      </div>
      <div className="row" style={{ gap: 10, marginTop: 18, alignItems: "center" }}>
        {confirmDel ? (
          <span className="row" style={{ gap: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>Delete this unit?</span>
            <button className="btn btn--ghost btn--sm" onClick={() => setConfirmDel(false)}>No</button>
            <button className="btn btn--sm" style={{ background: "var(--color-danger)", color: "#fff" }} disabled={deleting} onClick={onDelete}>{deleting ? "Deleting…" : "Yes, delete"}</button>
          </span>
        ) : (
          <button className="btn btn--ghost btn--sm" style={{ color: "var(--color-danger)" }} onClick={() => setConfirmDel(true)}>Delete</button>
        )}
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </Modal>
  );
}

// Add a new unit: creates a Unit + its first tenancy under a property (attach a meter afterward via Swap meter).
function NewUnitModal({ open, onClose, onCreated, mgr }) {
  const toast = useToast();
  const [props, setProps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    fetchProperties().then((p) => setProps(p || [])).catch(() => setProps([]));
    fetchTemplates().then((t) => setTemplates(t || [])).catch(() => setTemplates([]));
    const t = new Date();
    setF({ propertyId: "", unitName: "", qboCustomerName: "", qboCustomerRef: null, invoiceDefinitionId: "",
           moveInDate: `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`,
           invoiceDay: 1, minPeriod: 0, autoInvoice: true, autoEmail: true, autoPay: false });
    setBusy(false);
  }, [open]);
  if (!open || !f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const propRealm = (props.find((p) => String(p.id) === String(f.propertyId)) || {}).realmId || null;   // the property's company — scopes the customer picker + template list
  const realmTemplates = propRealm ? templates.filter((t) => String(t.realmId) === String(propRealm)) : templates;

  const create = () => {
    if (!f.propertyId) { toast({ title: "Property required", body: "Pick the property this unit belongs to." }); return; }
    if (!f.unitName.trim()) { toast({ title: "Unit name required", body: "Give the unit a name (e.g. A, B, 101)." }); return; }
    if (!f.invoiceDefinitionId) { toast({ title: "Template required", body: "Pick a rate template." }); return; }
    if (!f.moveInDate) { toast({ title: "Move-in required", body: "Enter a move-in date." }); return; }
    setBusy(true);
    createUnit({
      propertyId: +f.propertyId, unitName: f.unitName.trim(), qboCustomerName: f.qboCustomerName || null, qboCustomerRef: f.qboCustomerRef || null,
      invoiceDefinitionId: f.invoiceDefinitionId, moveInDate: f.moveInDate, invoiceDay: +f.invoiceDay,
      minPeriod: +f.minPeriod, autoInvoice: f.autoInvoice, autoEmail: f.autoEmail, autoPay: f.autoPay,
    })
      .then((d) => { setBusy(false); toast({ title: "Unit created", body: `${d.name} added to ${d.propName}.` }); onCreated(d); })
      .catch((e) => {
        setBusy(false);
        const msg = e && e.status === 409 && e.body && e.body.message ? e.body.message : "The API rejected the new unit.";
        toast({ title: "Create failed", body: msg });
      });
  };

  const Flag = ({ label, k, hint }) => (
    <div className="between" style={{ padding: "6px 0", gap: 12 }}>
      <div><div style={{ fontSize: 13 }}>{label}</div>{hint && <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>{hint}</div>}</div>
      <Toggle on={f[k]} onChange={(v) => set(k, v)} />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Add unit" width={520}>
      <div style={{ display: "grid", gap: 12 }}>
        <div className="field"><label>Property</label>
          <select className="select" value={f.propertyId} onChange={(e) => setF((x) => ({ ...x, propertyId: e.target.value, qboCustomerRef: null, invoiceDefinitionId: "" }))}>
            <option value="">Select property…</option>
            {props.filter((p) => mgr === "all" || String(p.managerId) === String(mgr)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field"><label>Unit name</label><input className="input" value={f.unitName} onChange={(e) => set("unitName", e.target.value)} placeholder="e.g. A, B, 101" /></div>
          <div className="field"><label>Move-in</label><input className="input" type="date" value={f.moveInDate} onChange={(e) => set("moveInDate", e.target.value)} /></div>
        </div>
        <div className="field"><label>QBO customer</label>
          <QboRefPicker realmId={propRealm} type="Customer" value={f.qboCustomerRef} name={f.qboCustomerName}
            placeholder="Select or type a QuickBooks customer…"
            onChange={({ qboId, name }) => setF((x) => ({ ...x, qboCustomerRef: qboId, qboCustomerName: name }))} /></div>
        <div className="field"><label>Rate template</label>
          <select className="select" value={f.invoiceDefinitionId} onChange={(e) => set("invoiceDefinitionId", e.target.value)}>
            <option value="">Select template…</option>
            {realmTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {f.propertyId && realmTemplates.length === 0 && <span className="muted" style={{ fontSize: 11 }}>No templates for this company yet — create one on the Rate Templates screen.</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field"><label>Invoice day of month</label><input className="input" type="number" min="1" max="28" value={f.invoiceDay} onChange={(e) => set("invoiceDay", e.target.value)} /><span className="muted" style={{ fontSize: 11 }}>The day of the month this unit should be billed (1–28).</span></div>
          <div className="field"><label>Min days between bills</label><input className="input" type="number" min="0" value={f.minPeriod} onChange={(e) => set("minPeriod", e.target.value)} /><span className="muted" style={{ fontSize: 11 }}>Won't create a new bill until this many days after the last one.</span></div>
        </div>
        <div style={{ borderTop: "1px solid var(--color-border-light)", paddingTop: 4 }}>
          <Flag label="Auto-invoice" k="autoInvoice" />
          <Flag label="Auto-email invoice" k="autoEmail" />
          <Flag label="Auto-pay" k="autoPay" hint="Tenant pays automatically — the bill uses the auto-pay memo and isn't sent for manual payment." />
        </div>
        <div className="muted" style={{ fontSize: 11.5 }}>Attach a meter afterward from the unit's Meter history → Swap meter.</div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={busy} onClick={create}>{busy ? "Creating…" : "Create unit"}</button>
      </div>
    </Modal>
  );
}

// Retire the unit's active meter and attach a new one (picks a synced sensor + counter/scale).
function SwapMeterModal({ open, onClose, tenancyId, current, onDone }) {
  const toast = useToast();
  const [sensors, setSensors] = useState([]);
  const [sensorId, setSensorId] = useState("");
  const [counter, setCounter] = useState(1);
  const [scale, setScale] = useState(0.001);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    fetchSensors().then((rows) => setSensors(rows)).catch(() => setSensors([]));
    setSensorId("");
    setCounter(current ? current.counterIndex : 1);
    setScale(current ? current.scale : 0.001);
    setDate(new Date().toISOString().slice(0, 10));
  }, [open]);
  if (!open) return null;

  const save = () => {
    if (!sensorId) { toast({ title: "Pick a sensor", body: "Choose the new sensor to attach to this unit." }); return; }
    setSaving(true);
    swapMeter(tenancyId, { sensorId: +sensorId, counterIndex: +counter, scale: +scale, effectiveFrom: date })
      .then(() => { setSaving(false); onDone(); onClose(); toast({ title: "Meter swapped", body: `Sensor ${sensorId} attached; the previous meter was retired ${date}.` }); })
      .catch(() => { setSaving(false); toast({ title: "Swap failed", body: "The API rejected the swap — check the values and try again." }); });
  };

  const selected = sensors.find((s) => String(s.sensorId) === String(sensorId));
  const multiCounter = !!(selected && isMultiCounterApp(selected.monnitAppId));

  return (
    <Modal open={open} onClose={onClose} title="Swap meter" width={470}>
      {current
        ? <div className="muted" style={{ fontSize: 12.5, marginBottom: 14 }}>Currently on sensor <b className="mono">{current.sensorId}</b> · counter {current.counterIndex} · ×{current.scale}. Swapping retires it as of the effective date and attaches the new meter.</div>
        : <div className="muted" style={{ fontSize: 12.5, marginBottom: 14 }}>No active meter — this will attach the first one.</div>}
      <div style={{ display: "grid", gap: 13 }}>
        <div className="field"><label>New sensor</label>
          <select className="select" value={sensorId} onChange={(e) => {
            const id = e.target.value; setSensorId(id);
            const sel = sensors.find((s) => String(s.sensorId) === String(id));
            if (!sel || !isMultiCounterApp(sel.monnitAppId)) setCounter(1);   // single-counter sensors always use counter 1
          }}>
            <option value="">Select a sensor…</option>
            {sensors.filter((s) => isPulseCounterApp(s.monnitAppId)).map((s) => <option key={s.sensorId} value={s.sensorId}>{s.sensorId}{s.name ? " · " + s.name : ""}{s.propName ? " (" + s.propName + ")" : ""}</option>)}
          </select>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: multiCounter ? "1fr 1fr" : "1fr", gap: 12 }}>
          {multiCounter && (
            <div className="field"><label>Counter index</label>
              <select className="select" value={counter} onChange={(e) => setCounter(e.target.value)}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
          <div className="field"><label>Scale</label><input className="input mono" type="number" step="0.001" value={scale} onChange={(e) => setScale(e.target.value)} /></div>
        </div>
        <div className="field"><label>Effective date</label><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={saving || !sensorId} onClick={save}>{saving ? "Swapping…" : "Swap meter"}</button>
      </div>
    </Modal>
  );
}

// Add a manual / estimated / baseline reading to a unit — or, when `edit` is a reading, correct its date/value.
// (Type + Read-by are set only at add time; editing just fixes the date and HCF value.)
function AddReadingModal({ open, onClose, tenancyId, onDone, edit }) {
  const toast = useToast();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    setF(edit
      ? { date: ymd(new Date(edit.readAt)), raw: String(edit.scaled), typeId: edit.isBaseline ? 8 : 1, readBy: edit.readBy || "" }
      : { date: ymd(new Date()), raw: "", typeId: 1, readBy: "" });
    setBusy(false);
  }, [open, edit && edit.id]);
  if (!open || !f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = () => {
    if (f.raw === "" || isNaN(+f.raw)) { toast({ title: "Reading required", body: "Enter the meter reading." }); return; }
    setBusy(true);
    const req = edit
      ? updateReading(tenancyId, edit.id, { date: f.date, hcf: +f.raw })
      : addReading(tenancyId, { date: f.date, hcf: +f.raw, typeId: +f.typeId, readBy: f.readBy || null });
    req
      .then((r) => {
        setBusy(false); onDone(); onClose();
        const d = r && r.drift;   // manual read vs the pulse-computed value — how far the counter had drifted
        if (!edit && d != null && Math.abs(d) >= 1) {
          toast({ title: "Reading added — re-baselined", err: Math.abs(d) >= 5,
                  body: `Register is ${Math.abs(d).toFixed(1)} HCF ${d > 0 ? "above" : "below"} the pulse total${d > 0 ? " — that much went un-counted (missed pulses) since the last reading" : " (counter over-counted)"}. Meter re-baselined.` });
        } else {
          toast({ title: edit ? "Reading updated" : "Reading added", body: edit ? "The reading was corrected." : "The reading was recorded." });
        }
      })
      .catch(() => { setBusy(false); toast({ title: edit ? "Update failed" : "Add failed", body: "The API rejected the reading." }); });
  };

  return (
    <Modal open={open} onClose={onClose} title={edit ? "Edit reading" : "Add reading"} width={440}>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field"><label>Date</label><input className="input" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
          <div className="field"><label>Meter reading (HCF)</label><input className="input" type="number" step="0.1" value={f.raw} onChange={(e) => set("raw", e.target.value)} /></div>
        </div>
        <span className="muted" style={{ fontSize: 11, marginTop: -4 }}>Enter the reading in HCF (hundred cubic feet), as shown on the meter.</span>
        {!edit && (
          <Fragment>
            <div className="field"><label>Type</label>
              <select className="select" value={f.typeId} onChange={(e) => set("typeId", e.target.value)}>
                <option value={1}>Manual</option>
                <option value={4}>Estimated</option>
                <option value={8}>Baseline</option>
              </select>
            </div>
            <div className="field"><label>Read by (optional)</label><input className="input" value={f.readBy} onChange={(e) => set("readBy", e.target.value)} placeholder="e.g. your name" /></div>
          </Fragment>
        )}
      </div>
      <div className="row" style={{ justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={busy} onClick={save}>{busy ? "Saving…" : edit ? "Save reading" : "Add reading"}</button>
      </div>
    </Modal>
  );
}

// Create a property billing into a QBO company (units get added to it afterward via Add unit).
function NewPropertyModal({ open, onClose, onCreated, mgr }) {
  const toast = useToast();
  const [companies, setCompanies] = useState([]);
  const [f, setF] = useState({ realmId: "", name: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    fetchQboConnections(mgr && mgr !== "all" ? mgr : null).then((c) => setCompanies(c || [])).catch(() => setCompanies([]));
    setF({ realmId: "", name: "" }); setBusy(false);
  }, [open, mgr]);
  if (!open) return null;

  const create = () => {
    if (!f.realmId) { toast({ title: "Company required", body: "Pick the QuickBooks company this property bills into." }); return; }
    if (!f.name.trim()) { toast({ title: "Name required", body: "Enter a property name." }); return; }
    setBusy(true);
    createProperty(f.realmId, f.name.trim())
      .then((p) => { setBusy(false); toast({ title: "Property created", body: `${p.name} added — use Add unit to add units.` }); onCreated(p); })
      .catch(() => { setBusy(false); toast({ title: "Create failed", body: "The API rejected the new property." }); });
  };

  return (
    <Modal open={open} onClose={onClose} title="Add property" width={440}>
      <div style={{ display: "grid", gap: 12 }}>
        <div className="field"><label>Company (QuickBooks)</label>
          <select className="select" value={f.realmId} onChange={(e) => setF({ ...f, realmId: e.target.value })}>
            <option value="">Select company…</option>
            {companies.map((c) => <option key={c.realmId} value={c.realmId}>{c.companyName || `Realm ${c.realmId}`}{c.connected ? "" : " · disconnected"}</option>)}
          </select>
        </div>
        <div className="field"><label>Property name</label><input className="input" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. 1923 Teak" /></div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={busy} onClick={create}>{busy ? "Creating…" : "Create property"}</button>
      </div>
    </Modal>
  );
}

// Rename or delete a property (delete is blocked while it still has units).
function PropertyEditModal({ open, onClose, property, onDone }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  useEffect(() => { if (open && property) { setName(property.name); setBusy(false); setConfirmDel(false); } }, [open, property && property.id]);
  if (!open || !property) return null;

  const save = () => {
    if (!name.trim()) { toast({ title: "Name required", body: "Enter a property name." }); return; }
    setBusy(true);
    updateProperty(property.id, name.trim())
      .then(() => { setBusy(false); onDone(); onClose(); toast({ title: "Property renamed", body: `Now "${name.trim()}".` }); })
      .catch(() => { setBusy(false); toast({ title: "Save failed", body: "The API rejected the rename." }); });
  };
  const del = () => {
    setBusy(true);
    deleteProperty(property.id)
      .then(() => { setBusy(false); onDone(); onClose(); toast({ title: "Property deleted", body: `${property.name} was removed.` }); })
      .catch((e) => {
        setBusy(false); setConfirmDel(false);
        const msg = e && e.status === 409 && e.body && e.body.message ? e.body.message : "Couldn't delete this property.";
        toast({ title: "Delete blocked", body: msg });
      });
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit property" width={440}>
      <div className="field"><label>Property name</label><input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="row" style={{ gap: 10, marginTop: 18, alignItems: "center" }}>
        {confirmDel ? (
          <span className="row" style={{ gap: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>Delete this property?</span>
            <button className="btn btn--ghost btn--sm" onClick={() => setConfirmDel(false)}>No</button>
            <button className="btn btn--sm" style={{ background: "var(--color-danger)", color: "#fff" }} disabled={busy} onClick={del}>Yes, delete</button>
          </span>
        ) : (
          <button className="btn btn--ghost btn--sm" style={{ color: "var(--color-danger)" }} onClick={() => setConfirmDel(true)}>Delete</button>
        )}
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------ Properties screen
function Properties({ mgr, go, openUnit, params = {}, managers = [], setMgr }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("active");
  const [view, setView] = useState("grouped");
  const [live, setLive] = useState(null);   // array = real units from API · false = fetch failed · null = loading
  const [liveDetailId, setLiveDetailId] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [newOpen, setNewOpen] = useState(false);
  const [propsList, setPropsList] = useState([]);
  const [newPropOpen, setNewPropOpen] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [sensorHealth, setSensorHealth] = useState(null);   // sensorId -> mapped LIVE sensor, for the health dot
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchUnits(), fetchProperties()])
      .then(([u, p]) => { if (!cancelled) { setLive(u.map(mapUnit)); setPropsList(p || []); } })
      .catch(() => { if (!cancelled) setLive(false); });
    // The health dot must come from LIVE telemetry — joining live units against the mock DATA.sensor() showed
    // a green "ok" on sensors that had been silent for weeks.
    fetchSensors().then((rows) => {
      const newest = Math.max(0, ...rows.map((r) => (r.lastComm ? new Date(r.lastComm).getTime() : 0)));
      if (!cancelled) setSensorHealth(new Map(rows.map((r) => [r.sensorId, mapSensor(r, newest)])));
    }).catch(() => { if (!cancelled) setSensorHealth(null); });
    return () => { cancelled = true; };
  }, [refresh]);

  // Deep-link: opened from a sensor drawer / invoice run with a unit id → open that unit's live drawer.
  useEffect(() => { if (params.unitId != null) setLiveDetailId(params.unitId); }, [params.unitId]);

  const usingLive = Array.isArray(live);
  const onRow = usingLive ? (id) => setLiveDetailId(id) : openUnit;
  const detailRow = liveDetailId != null && Array.isArray(live) ? live.find((u) => u.id === liveDetailId) : null;

  // Deployed builds: an API failure must be an honest error, not mock data that looks live.
  if (!live && !import.meta.env.DEV) return <ApiDown loading={live !== false} />;

  let units = usingLive ? live : DATA.unitsFor(mgr);
  if (usingLive && mgr !== "all") units = units.filter((u) => u.managerId === mgr);
  if (filter === "active") units = units.filter((u) => u.active);
  if (filter === "autopay") units = units.filter((u) => u.autoPay);
  if (filter === "manual") units = units.filter((u) => !u.autoInvoice);
  if (filter === "inactive") units = units.filter((u) => !u.active);
  if (q) {
    const s = q.toLowerCase();
    units = units.filter((u) => u.name.toLowerCase().includes(s) || u.tenant.toLowerCase().includes(s) || u.propName.toLowerCase().includes(s) || String(u.sensorId).includes(s));
  }

  // group by property (units), then merge in genuinely-empty properties so they stay manageable
  const groups = {};
  units.forEach((u) => { (groups[u.propKey] = groups[u.propKey] || { id: u.propKey, name: u.propName, mgr: u.managerId, mgrName: u.managerName || (DATA.manager(u.managerId) && DATA.manager(u.managerId).name) || "", items: [] }).items.push(u); });
  if (usingLive) propsList.forEach((p) => {
    if ((mgr === "all" || p.managerId === mgr) && p.unitCount === 0 && !groups[p.id])
      groups[p.id] = { id: p.id, name: p.name, mgr: p.managerId, mgrName: (managers.find((m) => m.id === p.managerId) || {}).name || "", items: [] };
  });
  const groupList = Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));

  const Row = ({ u }) => {
    // Mock-rate invoice math is a mock-mode fallback only — live rows show the API's projected or a dash.
    const comp = !usingLive && u.pendingRead ? DATA.computeInvoice(u, u.pendingUsage, 1) : null;
    const sensor = usingLive ? (sensorHealth ? sensorHealth.get(u.sensorId) : null) : DATA.sensor(u.sensorId);
    return (
      <tr onClick={() => onRow(u.id)} style={{ opacity: u.active ? 1 : 0.55 }}>
        <td>
          <div className="cell-2"><b>{u.tenant}</b><span>{u.name}</span></div>
        </td>
        {view === "flat" && <td className="muted">{u.propName}</td>}
        <td onClick={(e) => e.stopPropagation()}>
          {/* hover = the diagnosis; click = the authoritative Sensor health drawer (same deep-link the
              Overview attention feed uses). The whole cell is the target — the 7px dot alone is untappable. */}
          <span className={`row${u.sensorId != null ? " sensor-link" : ""}`} style={{ gap: 7 }}
                title={u.sensorId == null ? undefined : `${!sensor ? "Health unknown"
                  : sensor.offline ? `Not reporting — last seen ${sensor.lastComm ? fmtShort(sensor.lastComm) : "never"}`
                  : sensor.lowBatt ? `Low battery — ${sensor.battery}%`
                  : `Reporting · battery ${sensor.battery}% · signal ${sensor.signal}%`}. Click for sensor health.`}
                onClick={u.sensorId != null && go ? () => go("sensors", { sensorId: u.sensorId }) : undefined}>
            <span className="mono" style={{ fontSize: 11.5 }}>{u.sensorId}</span>
            {!sensor ? <span className="pdot" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--color-border-light)" }} />
              : sensor.offline ? <span className="pdot" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--color-danger)" }} />
                : sensor.lowBatt ? <span className="pdot" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--color-warning-text)" }} />
                  : <span className="pdot" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--color-success)" }} />}
          </span>
        </td>
        <td>
          <span className="row" style={{ gap: 9 }}>
            <Sparkline data={u.reads.slice(-7).map((r) => r.usage)} w={64} h={22} color={u.pendingRead && u.pendingRead.spike ? "var(--color-danger)" : "var(--color-primary)"} />
            <b style={{ fontVariantNumeric: "tabular-nums" }}>{u.pendingUsage}</b>
          </span>
        </td>
        <td className="muted">{u.lastRead ? fmtShort(u.lastRead.date) : "\u2014"}</td>
        <td className="num strong">{u.projected != null ? fmtMoney(u.projected) : comp ? fmtMoney(comp.total) : "\u2014"}</td>
        <td>
          <div className="row" style={{ gap: 5 }}>
            {!u.active && <Pill kind="muted">Inactive</Pill>}
            {u.autoInvoice ? <Pill kind="auto">Auto</Pill> : <Pill kind="muted">Manual</Pill>}
            {u.autoPay && <Pill kind="scheduled">Pay</Pill>}
          </div>
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <button className="rowmenu" onClick={() => onRow(u.id)}><Icon name="chevron" size={16} /></button>
        </td>
      </tr>
    );
  };

  const cols = view === "flat" ? 8 : 7;

  // Export the currently-filtered units to a CSV the browser downloads (usage is in HCF).
  const exportCsv = () => {
    const head = ["Property", "Unit", "Tenant", "Sensor", "This period (HCF)", "Projected", "Last read", "Auto-invoice", "Auto-pay", "Active"];
    const rows = units.map((u) => [
      u.propName, u.name, u.tenant, u.sensorId, u.pendingUsage,
      u.projected != null ? u.projected : "", u.lastRead ? fmtShort(u.lastRead.date) : "",
      u.autoInvoice ? "yes" : "no", u.autoPay ? "yes" : "no", u.active ? "yes" : "no",
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = "units.csv"; a.click(); URL.revokeObjectURL(a.href);
    toast({ title: "Exported", body: `${units.length} unit${units.length === 1 ? "" : "s"} exported to units.csv.` });
  };

  return (
    <Fragment>
    <div className="page">
      <div className="page__head">
        <div>
          {mgr !== "all" && setMgr && <button className="btn btn--ghost btn--sm" style={{ marginBottom: 5, padding: "2px 8px" }} title="Back to all companies" onClick={() => setMgr("all")}>‹ All companies</button>}
          <h1>Properties &amp; Units</h1>
          <p>{units.length} unit{units.length === 1 ? "" : "s"} · {groupList.length} propert{groupList.length === 1 ? "y" : "ies"}{mgr !== "all" ? " · " + ((managers.find((m) => m.id === mgr) || {}).name || "") : ""}{usingLive ? " · Live (API)" : live === false ? " · mock (API offline)" : ""}</p>
        </div>
        <div className="right">
          <button className="btn btn--ghost" onClick={exportCsv}><Icon name="download" size={16} />Export</button>
          {usingLive && <button className="btn btn--soft" onClick={() => setNewPropOpen(true)}><Icon name="plus" size={16} />Add property</button>}
          <button className="btn btn--primary" onClick={() => usingLive ? setNewOpen(true) : toast({ title: "Add unit", body: "Connect to the API to add units." })}><Icon name="plus" size={16} />Add unit</button>
        </div>
      </div>

      <div className="tbl-wrap">
        <div className="tbl-tools">
          <Search value={q} onChange={setQ} placeholder="Search tenant, unit, sensor…" />
          <div className="seg">
            {[["active", "Active"], ["autopay", "Auto-pay"], ["manual", "Manual"], ["inactive", "Inactive"], ["all", "All"]].map(([k, l]) => (
              <button key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>{l}</button>
            ))}
          </div>
          <div className="spacer" />
          <span className="count">{units.length} shown</span>
          <div className="seg">
            <button className={view === "grouped" ? "on" : ""} onClick={() => setView("grouped")}>Grouped</button>
            <button className={view === "flat" ? "on" : ""} onClick={() => setView("flat")}>Flat</button>
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Tenant / Unit</th>
              {view === "flat" && <th>Property</th>}
              <th>Sensor</th>
              <th>This period</th>
              <th>Last read</th>
              <th className="num">Projected</th>
              <th>Billing</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {view === "grouped" ? groupList.map((g) => (
              <Fragment key={g.name}>
                <tr className="grouprow"><td colSpan={cols}>
                  <div className="between" style={{ alignItems: "center" }}>
                    <span className="row" style={{ gap: 8 }}><Avatar id={g.mgr} label={initials(g.mgrName)} size={18} round={4} />{g.name} <span className="muted" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>· {g.items.length} unit{g.items.length !== 1 ? "s" : ""}</span></span>
                    <span className="row" style={{ gap: 2 }}>
                      {g.id != null && <button className="rowmenu" title="View this property's usage over time" onClick={(e) => { e.stopPropagation(); go && go("usage", { propertyId: g.id }); }}><Icon name="chart" size={14} /></button>}
                      {usingLive && g.id != null && <button className="rowmenu" title="Edit property" onClick={(e) => { e.stopPropagation(); setEditProp(g); }}><Icon name="edit" size={14} /></button>}
                    </span>
                  </div>
                </td></tr>
                {g.items.map((u) => <Row key={u.id} u={u} />)}{/* grouped rows */}
              </Fragment>
            )) : units.map((u) => <Row key={u.id} u={u} />)}
            {units.length === 0 && <tr><td colSpan={cols}><div className="empty">No units match your filter.</div></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
      <LiveUnitDrawer tenancyId={liveDetailId} onClose={() => setLiveDetailId(null)} onChanged={() => setRefresh((n) => n + 1)} go={go}
        projected={detailRow ? detailRow.projected : null} usage={detailRow ? detailRow.pendingUsage : null} />
      <NewUnitModal open={newOpen} mgr={mgr} onClose={() => setNewOpen(false)}
        onCreated={(d) => { setNewOpen(false); setRefresh((n) => n + 1); setLiveDetailId(d.id); }} />
      <NewPropertyModal open={newPropOpen} mgr={mgr} onClose={() => setNewPropOpen(false)}
        onCreated={() => { setNewPropOpen(false); setRefresh((n) => n + 1); }} />
      <PropertyEditModal open={!!editProp} onClose={() => setEditProp(null)} property={editProp}
        onDone={() => setRefresh((n) => n + 1)} />
    </Fragment>
  );
}

export { Properties };
